import { z } from 'zod';

import { AiImageSchema } from './ai-image';
import { AiRequestBaseSchema } from './ai-request';

/**
 * Image embedding — the `embedImage` half of the provider interface, served by
 * the `face-embedding` capability.
 *
 * **A separate capability from `ai-embedding`, not a widening of it.** The two
 * take incompatible inputs, so one `embed` accepting a string *or* an image
 * would be a discriminated union at every call site — and an operator must be
 * able to point face-match at one model while semantic search uses another
 * (N21: one active provider *per capability*).
 *
 * The image's shape and its base64 rationale live in `AiImageSchema`.
 */
export const AiImageEmbeddingRequestSchema = AiRequestBaseSchema.extend({
  image: AiImageSchema,
  /**
   * The requested vector width.
   *
   * Left unset the model's native width is used. Two vectors can only be
   * compared when they were produced at the **same** width, so a caller that
   * compares must pin it and pass the same value to both calls — and a stored
   * vector's width is fixed for ever, because a pgvector column is fixed-width.
   */
  dimensions: z.number().int().min(1).max(4_096).optional(),
}).strict();

export type AiImageEmbeddingRequest = z.infer<typeof AiImageEmbeddingRequestSchema>;

export const AiImageEmbeddingResultSchema = z
  .object({
    vector: z.array(z.number()).min(1),
    /** Always `vector.length` — carried explicitly so a caller need not measure. */
    dimensions: z.number().int().min(1),
  })
  .strict();

export type AiImageEmbeddingResult = z.infer<typeof AiImageEmbeddingResultSchema>;
